import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useItems } from '../../hooks/useItems';
import { useCustomers } from '../../hooks/useCustomers';
import { useAuth } from '../../contexts/AuthContext';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, CellMouseOverEvent, CellMouseDownEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { CustomerFormDrawer } from '../Customers/components/CustomerFormDrawer';
import ItemFormDrawer from '../Items/components/ItemFormDrawer';
import { useCategories } from '../../hooks/useCategories';
import { useApiClient } from '../../utils/apiClient';

interface GridStepProps {
  sessionId: string;
  rows: any[];
  mapping: any;
  onValidated: () => void;
  initialSpreadsheetRows?: SpreadsheetRow[];
  onSpreadsheetRowsChange?: (rows: SpreadsheetRow[]) => void;
  containsDates?: boolean;
  monthStart?: string;
}

interface SpreadsheetRow {
  rowId: string; // stable identifier for row updates and caches
  customer: string;
  customerId: string | null;
  customerLookupKey: string;
  [dateKey: string]: any; // Dynamic date columns
}

interface ServiceEntry {
  type: string;
  qty: number;
  rate: number;
  isTaxable: boolean;
  description?: string;
  pendingName?: string;
  needsInput?: boolean;
  forcedItemLabel?: string;
  _key?: string;
}

interface ItemPricingInfo {
  itemName: string;
  baseRate: number;
  customerRate: number | null;
  effectiveRate: number;
  rate: number;
  isTaxable: boolean;
  hasCustomerOverride: boolean;
}

interface RateConflictPromptState {
  itemName: string;
  currentRate: number;
  effectiveRate: number;
  customerName?: string;
  serviceDate?: string;
  lineNumber?: number;
}

function extractQuotedDescription(label?: string): string | undefined {
  const trimmed = label?.trim();
  if (!trimmed) return undefined;
  const match = trimmed.match(/^"(.+?)"$/) || trimmed.match(/^'(.*?)'$/);
  return match ? (match[1] || match[2] || '').trim() : undefined;
}

// Generate a deterministic pastel color from a string key
function pastelColorFromKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  const s = 60; // saturation
  const l = 90; // lightness for pastel
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function generateKey(): string {
  try {
    // use crypto if available for stability
    return (window as any).crypto?.randomUUID?.() || `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  } catch {
    return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

export default function GridStep({ sessionId, rows, mapping, onValidated, initialSpreadsheetRows, onSpreadsheetRowsChange, containsDates = true, monthStart }: GridStepProps) {
  const [spreadsheetRows, setSpreadsheetRows] = useState<SpreadsheetRow[]>([]);
  const spreadsheetRowsRef = useRef<SpreadsheetRow[]>([]);
  const [dateColumnsState, setDateColumnsState] = useState<{ field: string; headerName: string }[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [isPainting, setIsPainting] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [splitCellModal, setSplitCellModal] = useState<{ rowId: string; dateKey: string } | null>(null);
  const [customerModal, setCustomerModal] = useState<{ rowId: string; customerKey: string } | null>(null);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const [pendingCustomerRowId, setPendingCustomerRowId] = useState<string | null>(null);
  const [isItemDrawerOpen, setIsItemDrawerOpen] = useState(false);
  const [itemDrawerInitial, setItemDrawerInitial] = useState<string>('');
  const pendingPlaceholderRef = useRef<{ rowId: string; dateKey: string; index: number } | null>(null);
  const rateConflictResolverRef = useRef<((choice: 'keep' | 'use') => void) | null>(null);
  const [rateConflictPrompt, setRateConflictPrompt] = useState<RateConflictPromptState | null>(null);
  const [layoutMode, setLayoutMode] = useState<'columns' | 'cell'>(() => {
    try { return (localStorage.getItem('massGridLayout') as any) || 'columns'; } catch { return 'columns'; }
  });
  useEffect(() => {
    try { localStorage.setItem('massGridLayout', layoutMode); } catch {}
  }, [layoutMode]);
  // Auto-switch to Services-in-cell in no-dates mode for better sizing/UX
  useEffect(() => {
    if (!containsDates && layoutMode !== 'cell') {
      setLayoutMode('cell');
    }
  }, [containsDates, layoutMode]);
  const [baseServiceLabel, setBaseServiceLabel] = useState<string>(() => {
    try { return localStorage.getItem('massGridBaseService') || 'Grass Cut'; } catch { return 'Grass Cut'; }
  });
  useEffect(() => {
    try { localStorage.setItem('massGridBaseService', baseServiceLabel); } catch {}
  }, [baseServiceLabel]);
  useEffect(() => {
    spreadsheetRowsRef.current = spreadsheetRows;
  }, [spreadsheetRows]);
  const [highlightTarget, setHighlightTarget] = useState<{ rowId: string; dateKey: string } | null>(null);
  const highlightTargetRef = useRef<{ rowId: string; dateKey: string } | null>(null);
  // Track collapsed state per rowId -> { [dateKey]: boolean }
  const collapsedPanelsRef = useRef<Map<string, Record<string, boolean>>>(new Map());
  // Grid zoom level (1.0 = 100%)
  const [zoom, setZoom] = useState<number>(1);
  const zoomIn = useCallback(() => setZoom(z => Math.min(1.5, Math.round((z + 0.1) * 10) / 10)), []);
  const zoomOut = useCallback(() => setZoom(z => Math.max(0.7, Math.round((z - 0.1) * 10) / 10)), []);
  const zoomReset = useCallback(() => setZoom(1), []);

  // Helper: get this row's date keys sorted ascending by actual date
  const getRowDateKeysSorted = useCallback((row: SpreadsheetRow): string[] => {
    const keys = Object.keys(row).filter(k => k.startsWith('date_'));
    const toTime = (dk: string) => {
      const iso = dk.replace('date_', '');
      const d = new Date(iso);
      return isNaN(d.getTime()) ? Number.MAX_SAFE_INTEGER : d.getTime();
    };
    return keys.sort((a,b) => toTime(a) - toTime(b));
  }, []);

  // Services-in-cell: number of shared slot columns equals max dates across any row
  const cellSlotsCount = useMemo(() => {
    if (layoutMode !== 'cell') return 0;
    let max = 0;
    for (const r of spreadsheetRows) {
      const count = Object.keys(r).filter(k => k.startsWith('date_')).length;
      if (count > max) max = count;
    }
    return max;
  }, [layoutMode, spreadsheetRows]);

  // Selection for Services-in-cell
  const [selectedCells, setSelectedCells] = useState<string[]>([]); // keys: rowId::dateKey
  const makeKey = (rowId: string, dk: string) => `${rowId}::${dk}`;
  const isSelected = (rowId: string, dk: string) => selectedCells.includes(makeKey(rowId, dk));
  // Row-level paste anchor for rows without dates
  const [selectedRowAnchor, setSelectedRowAnchor] = useState<string | null>(null);
  const selectedRowAnchorRef = useRef<string | null>(null);

  // Clipboard support
  type CopiedCell = { rowId: string; dateKey: string; services: ServiceEntry[] };
  const buildCopyPayload = (cells: CopiedCell[]) => ({ __invoiceMaker: 'cells', cells });

  // Undo support
  type UndoEntry = { rowId: string; dateKey: string; prev: ServiceEntry[] };
  type UndoBatch = { entries: UndoEntry[]; addedDateKeysByRow: Record<string,string[]> };
  const [undoStack, setUndoStack] = useState<UndoBatch[]>([]);
  const serviceEditorQueryCacheRef = useRef<Record<string, string>>({});

  const pushUndo = useCallback((entries: UndoEntry[], added: Record<string,string[]>) => {
    setUndoStack(prev => [...prev, { entries, addedDateKeysByRow: added }]);
  }, []);

  const handleUndo = useCallback(() => {
    if (!undoStack.length) return;
    const last = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, prev.length - 1));
    setSpreadsheetRows(prev => prev.map(r => {
      const addedKeys = last.addedDateKeysByRow[r.rowId] || [];
      let changed = false;
      const copy: any = { ...r };
      // Restore services
      for (const e of last.entries) {
        if (e.rowId !== r.rowId) continue;
        copy[e.dateKey] = e.prev;
        changed = true;
      }
      // Remove dates added in this batch
      for (const k of addedKeys) {
        if (copy[k]) { delete copy[k]; changed = true; }
      }
      return changed ? (copy as SpreadsheetRow) : r;
    }));
    gridRef.current?.api?.resetRowHeights();
  }, [undoStack]);

  // Overwrite confirmation modal
  const [confirmOverwrite, setConfirmOverwrite] = useState<{ count: number; proceed: (()=>void) | null } | null>(null);

  // Keyboard: copy/paste/undo
  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      const tag = (document.activeElement && (document.activeElement as HTMLElement).tagName) || '';
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag);
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'c' && !isTyping && layoutMode === 'cell') {
        e.preventDefault();
        // Build copy cells from selection
        const cells: CopiedCell[] = [];
        for (const key of selectedCells) {
          const [rowId, dk] = key.split('::');
          const row = spreadsheetRows.find(r => r.rowId === rowId);
          if (!row) continue;
          const services: ServiceEntry[] = (row as any)[dk] ? JSON.parse(JSON.stringify((row as any)[dk])) : [];
          cells.push({ rowId, dateKey: dk, services });
        }
        const payload = buildCopyPayload(cells);
        try {
          await navigator.clipboard.writeText(JSON.stringify(payload));
        } catch {
          // fallback text
          await navigator.clipboard.writeText(cells.map(c => `${c.dateKey}: ${c.services.length} service(s)`).join('\n'));
        }
        return;
      }
      if (mod && e.key.toLowerCase() === 'v' && !isTyping && layoutMode === 'cell') {
        e.preventDefault();
        // Read clipboard
        let text = '';
        try { text = await navigator.clipboard.readText(); } catch {}
        let data: any = null;
        try { data = JSON.parse(text); } catch { data = null; }
        if (!data || data.__invoiceMaker !== 'cells' || !Array.isArray(data.cells) || data.cells.length === 0) return;

        const targets = selectedCells.slice();
        if (targets.length === 0) return;

        // Determine overwrites in current targets only (existing cells non-empty)
        let overwriteCount = 0;
        for (let i = 0; i < Math.min(targets.length, data.cells.length); i++) {
          const [rowId, dk] = targets[i].split('::');
          const row = spreadsheetRows.find(r => r.rowId === rowId);
          const list: ServiceEntry[] = row ? ((row as any)[dk] || []) : [];
          const hasContent = Array.isArray(list) && list.some(s => !!s?.type);
          if (hasContent) overwriteCount++;
        }

        const executePaste = async () => {
          const sources: CopiedCell[] = data.cells;
          const undoEntries: UndoEntry[] = [];
          const addedByRow: Record<string,string[]> = {};
          const appliedCells: { rowId: string; dateKey: string; customerId: string | null; services: { index: number; type?: string }[] }[] = [];

          setSpreadsheetRows(prev => {
            const rowsCopy = prev.map(r => ({ ...r } as any));
            // Build helper to add date to a row and track additions
            const ensureDateOnRow = (row: any, dk: string) => {
              if (!row[dk]) row[dk] = [];
            };
            // Use a local targets array so we can seed it when no dates selected
            const localTargets = targets.slice();
            if (localTargets.length === 0 && selectedRowAnchor) {
              const anchorRow = rowsCopy.find(r => r.rowId === selectedRowAnchor);
              if (anchorRow) {
                if (!addedByRow[selectedRowAnchor]) addedByRow[selectedRowAnchor] = [];
                for (const src of sources) {
                  const dk = src.dateKey;
                  if (dk) {
                    if (!anchorRow[dk]) {
                      anchorRow[dk] = [];
                      addedByRow[selectedRowAnchor].push(dk);
                    }
                    localTargets.push(makeKey(selectedRowAnchor, dk));
                  }
                }
              }
            }
            // Apply paste across targets; expand N>M by adding new date keys using source dateKeys
            let tIndex = 0;
            for (let sIndex = 0; sIndex < sources.length; sIndex++) {
              const src = sources[sIndex];
              if (tIndex >= localTargets.length) {
                // No more targets: expand by adding to the row of the first selected cell (spreadsheet behavior)
                const [anchorRowId] = (localTargets[0] || targets[0] || `${selectedRowAnchor || ''}::`).split('::');
                const anchorRow = rowsCopy.find(r => r.rowId === anchorRowId);
                if (!anchorRow) break;
                const dk = src.dateKey; // use source date key
                if (!addedByRow[anchorRowId]) addedByRow[anchorRowId] = [];
                if (!anchorRow[dk]) {
                  anchorRow[dk] = [];
                  addedByRow[anchorRowId].push(dk);
                }
                localTargets.push(makeKey(anchorRowId, dk));
              }
              const [rowId, dk] = localTargets[tIndex].split('::');
              const row = rowsCopy.find(r => r.rowId === rowId) as any;
              if (!row) { tIndex++; continue; }
              ensureDateOnRow(row, dk);
              // Save undo
              undoEntries.push({ rowId, dateKey: dk, prev: JSON.parse(JSON.stringify(row[dk])) });
              // Resolve rates per target customer
              const resolved: ServiceEntry[] = [];
              for (const svc of (src.services || [])) {
                resolved.push({ ...svc });
              }
              row[dk] = resolved;
              // Track cells to adjust pricing for after paste
              appliedCells.push({
                rowId,
                dateKey: dk,
                customerId: row.customerId ?? null,
                services: resolved.map((s, index) => ({ index, type: s?.type }))
              });
              tIndex++;
            }
            spreadsheetRowsRef.current = rowsCopy as SpreadsheetRow[];
            return rowsCopy as SpreadsheetRow[];
          });

          // Resolve item pricing after structural update so imported/manual rates stay protected.
          setTimeout(async () => {
            for (const cell of appliedCells) {
              const rowSnapshot = spreadsheetRowsRef.current.find(r => r.rowId === cell.rowId);
              if (!rowSnapshot) continue;
              const currentList: ServiceEntry[] = Array.isArray((rowSnapshot as any)[cell.dateKey]) ? ([...(rowSnapshot as any)[cell.dateKey]] as ServiceEntry[]) : [];
              let changed = false;
              for (const svc of cell.services) {
                if (!svc.type) continue;
                const currentLine = currentList[svc.index];
                if (!currentLine) continue;
                const nextLine = await resolveAssignedServiceLine(currentLine, cell.customerId, svc.type);
                if (!didServiceLineChange(currentLine, nextLine)) continue;
                currentList[svc.index] = nextLine;
                changed = true;
              }
              if (!changed) continue;
              setSpreadsheetRows(prev => {
                const rowsCopy = prev.map(r => ({ ...r } as any));
                const row = rowsCopy.find((rr: any) => rr.rowId === cell.rowId);
                if (!row) return prev;
                row[cell.dateKey] = currentList;
                spreadsheetRowsRef.current = rowsCopy as SpreadsheetRow[];
                return rowsCopy as SpreadsheetRow[];
              });
            }
            pushUndo(undoEntries, addedByRow);
            gridRef.current?.api?.resetRowHeights();
          }, 0);
        };

        if (overwriteCount > 0) {
          setConfirmOverwrite({ count: overwriteCount, proceed: executePaste });
        } else {
          executePaste();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [layoutMode, selectedCells, spreadsheetRows, pushUndo, selectedRowAnchor]);
  // Base catalog items (not price-aware) to populate selectors/brush list
  const { items, mutate: mutateItems } = useItems();
  const { customers } = useCustomers();
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const { categories = [] } = useCategories('item');

  const gridRef = useRef<AgGridReact<any>>(null);
  // Coalesce grid refresh calls to avoid nested updates
  const scheduledRefreshRef = useRef<number | null>(null);
  const refreshGridCells = useCallback(() => {
    if (scheduledRefreshRef.current != null) return;
    scheduledRefreshRef.current = window.setTimeout(() => {
      try {
        gridRef.current?.api?.refreshCells({ suppressFlash: true });
      } finally {
        if (scheduledRefreshRef.current != null) {
          clearTimeout(scheduledRefreshRef.current);
          scheduledRefreshRef.current = null;
        }
      }
    }, 0);
  }, []);
  // Coalesce row height resets so AG Grid re-measures after structure changes
  const scheduledResetHeightsRef = useRef<number | null>(null);
  const resetRowHeights = useCallback(() => {
    if (scheduledResetHeightsRef.current != null) return;
    scheduledResetHeightsRef.current = window.setTimeout(() => {
      try {
        gridRef.current?.api?.resetRowHeights();
      } finally {
        if (scheduledResetHeightsRef.current != null) {
          clearTimeout(scheduledResetHeightsRef.current);
          scheduledResetHeightsRef.current = null;
        }
      }
    }, 0);
  }, []);
  // Stable fallback row id to avoid thrashing row identities across renders
  const fallbackRowIdRef = useRef<string>(`row#${Math.random().toString(36).slice(2)}`);

  // Reflect highlight target and refresh grid without forcing, after helpers are declared
  useEffect(() => {
    highlightTargetRef.current = highlightTarget;
    refreshGridCells();
  }, [highlightTarget, refreshGridCells]);

  useEffect(() => {
    selectedRowAnchorRef.current = selectedRowAnchor;
    refreshGridCells();
  }, [selectedRowAnchor, refreshGridCells]);

  // Derived default base item label
  const DEFAULT_BASE_ITEM_NAME = baseServiceLabel;

  const parseFlexibleDate = useCallback((raw: string): string | null => {
    if (!raw) return null;
    const s = String(raw).trim();
    const ymd = s.match(/^\d{4}-\d{1,2}-\d{1,2}$/);
    if (ymd) {
      const [y,m,d] = s.split('-').map(Number);
      const dt = new Date(y, m-1, d);
      return isNaN(dt.getTime()) ? null : `${dt.getFullYear()}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    }
    const mdy = s.match(/^(\d{1,2})[\/-](\d{1,2})(?:[\/-](\d{2,4}))?$/);
    if (mdy) {
      const month = Number(mdy[1]);
      const day = Number(mdy[2]);
      let year = mdy[3] ? Number(mdy[3]) : new Date().getFullYear();
      if (year < 100) year = 2000 + year;
      const dt = new Date(year, month-1, day);
      return isNaN(dt.getTime()) ? null : `${dt.getFullYear()}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    }
    return null;
  }, []);

  type ParsedSegment = { date?: string; qty?: number; price?: number; label?: string; forcedItemLabel?: string };
  const parseSemicolonCell = useCallback((raw: string, inheritDate?: string): ParsedSegment[] => {
    if (!raw) return [];
    const parts = String(raw).split(/;\s*/).filter(Boolean);
    const segments: ParsedSegment[] = [];
    let lastDate = inheritDate;
    for (const p of parts) {
      let s = p.trim();
      // Try to extract a date token
      const tokens = s.split(/\s+/);
      let segDate: string | undefined;
      if (tokens.length && parseFlexibleDate(tokens[0])) {
        segDate = parseFlexibleDate(tokens.shift()! ) || undefined;
        s = tokens.join(' ').trim();
        lastDate = segDate || lastDate;
      }
      const qtyMatch = s.match(/\bx(\d+)\b/i);
      const qty = qtyMatch ? Number(qtyMatch[1]) : 1;
      s = s.replace(/\bx\d+\b/i, '').trim();
      const priceMatch = s.match(/(?:\$\s*)?(\d+(?:\.\d{1,2})?)\s*$/);
      let price: number | undefined;
      if (priceMatch) {
        price = Number(priceMatch[1]);
        // Remove trailing price text to get label
        s = s.slice(0, s.lastIndexOf(priceMatch[0])).trim();
      }
      // Detect explicit item marker: !ITEM NAME!
      let forcedItemLabel: string | undefined;
      const forceMatch = s.match(/!(.+?)!/);
      if (forceMatch) {
        forcedItemLabel = forceMatch[1].trim();
        // Remove the forced marker from remaining label text
        s = (s.slice(0, forceMatch.index) + s.slice((forceMatch.index || 0) + forceMatch[0].length)).trim();
      }
      const label = s.trim();
      segments.push({ date: segDate || lastDate, qty, price, label: label || undefined, forcedItemLabel });
    }
    return segments;
  }, [parseFlexibleDate]);

  // Helper to create a safe column field ID from a date string
  const getDateColId = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return `date_${d.toISOString().slice(0, 10)}`;
    return `date_${String(dateStr).replace(/[^0-9A-Za-z]/g, '_')}`;
  }, []);

  const getServiceEditorKey = useCallback((rowId: string, dateKey: string, service: ServiceEntry | undefined, index: number) => {
    return service?._key || `${rowId}::${dateKey}::${index}`;
  }, []);

  // Helper: ignore brush when interacting with form controls
  const isFormControlTarget = useCallback((target: EventTarget | null | undefined) => {
    const el = target as HTMLElement | null | undefined;
    if (!el || typeof el.closest !== 'function') return false;
    return !!el.closest('input,select,textarea,button,[contenteditable="true"],[data-service-editor]');
  }, []);

  // Cache of price-aware items per customer
  const customerItemsCacheRef = useRef<Map<string, any[]>>(new Map());
  // Customer autocomplete: per-cell state handled inside CustomerRenderer to ensure immediate re-render
  const debouncers = useRef<Record<string, any>>({});
  const api = useApiClient();

  const isCustomerTaxExempt = useCallback((customerId: string | null | undefined): boolean => {
    if (!customerId) return false;
    return !!customers?.some((customer: any) => customer.id === customerId && customer.tax_exempt === true);
  }, [customers]);

  const normalizeTaxable = useCallback((customerId: string | null | undefined, isTaxable: boolean | null | undefined): boolean => {
    if (isCustomerTaxExempt(customerId)) return false;
    return isTaxable !== false;
  }, [isCustomerTaxExempt]);

  const ratesEqual = useCallback((a: number | null | undefined, b: number | null | undefined): boolean => {
    return Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.0001;
  }, []);

  const formatRate = useCallback((value: number | null | undefined): string => {
    return `$${(Number(value) || 0).toFixed(2)}`;
  }, []);

  const fetchCustomerItems = useCallback(async (customerId: string): Promise<any[]> => {
    if (!customerId) return [];
    const cached = customerItemsCacheRef.current.get(customerId);
    if (cached) return cached;
    const list = await api.get<any[]>(`/items?customer_id=${encodeURIComponent(customerId)}`);
    customerItemsCacheRef.current.set(customerId, list);
    return list;
  }, [api]);

  const getItemPricingInfo = useCallback(async (customerId: string | null, itemId: string): Promise<ItemPricingInfo | null> => {
    const list = customerId ? await fetchCustomerItems(customerId) : [];
    const found = list.find((i: any) => i.id === itemId);
    if (found) {
      const baseRate = Number(found.standard_rate ?? found.rate) || 0;
      const effectiveRate = typeof found.effective_rate === 'number' ? found.effective_rate : Number(found.rate) || 0;
      const customerRateRaw = found.customer_rate;
      const customerRate = customerRateRaw === null || customerRateRaw === undefined || customerRateRaw === ''
        ? null
        : Number(customerRateRaw);
      return {
        itemName: String(found.name || ''),
        baseRate,
        customerRate,
        effectiveRate,
        rate: effectiveRate < 0 ? 0 : effectiveRate,
        isTaxable: normalizeTaxable(customerId, !!found.is_taxable),
        hasCustomerOverride: customerRate !== null && !ratesEqual(effectiveRate, baseRate),
      };
    }

    const base = items.find((i: any) => i.id === itemId);
    if (base) {
      const baseRate = Number((base as any).standard_rate ?? (base as any).rate) || 0;
      return {
        itemName: String((base as any).name || ''),
        baseRate,
        customerRate: null,
        effectiveRate: baseRate,
        rate: baseRate < 0 ? 0 : baseRate,
        isTaxable: normalizeTaxable(customerId, !!(base as any).is_taxable),
        hasCustomerOverride: false,
      };
    }

    return null;
  }, [fetchCustomerItems, items, normalizeTaxable, ratesEqual]);

  const getEffectiveRate = useCallback(async (customerId: string | null, itemId: string): Promise<{ rate: number; is_taxable: boolean } | null> => {
    const pricing = await getItemPricingInfo(customerId, itemId);
    if (!pricing) return null;
    return { rate: pricing.rate, is_taxable: pricing.isTaxable };
  }, [getItemPricingInfo]);

  const resolveRateConflictPrompt = useCallback((choice: 'keep' | 'use') => {
    const resolve = rateConflictResolverRef.current;
    rateConflictResolverRef.current = null;
    setRateConflictPrompt(null);
    resolve?.(choice);
  }, []);

  useEffect(() => {
    return () => {
      if (rateConflictResolverRef.current) {
        rateConflictResolverRef.current('keep');
        rateConflictResolverRef.current = null;
      }
    };
  }, []);

  const promptForRateConflict = useCallback((prompt: RateConflictPromptState) => {
    return new Promise<'keep' | 'use'>((resolve) => {
      rateConflictResolverRef.current = resolve;
      setRateConflictPrompt(prompt);
    });
  }, []);

  const didServiceLineChange = useCallback((prevLine: ServiceEntry | undefined, nextLine: ServiceEntry | undefined): boolean => {
    if (!prevLine || !nextLine) return prevLine !== nextLine;
    return (
      prevLine.type !== nextLine.type ||
      !ratesEqual(prevLine.rate, nextLine.rate) ||
      !!prevLine.isTaxable !== !!nextLine.isTaxable ||
      (prevLine.pendingName || '') !== (nextLine.pendingName || '') ||
      !!prevLine.needsInput !== !!nextLine.needsInput
    );
  }, [ratesEqual]);

  const resolveAssignedServiceLine = useCallback(async (
    line: ServiceEntry,
    customerId: string | null,
    itemId: string,
    promptContext?: Pick<RateConflictPromptState, 'customerName' | 'serviceDate' | 'lineNumber'>
  ): Promise<ServiceEntry> => {
    const nextBase: ServiceEntry = {
      ...line,
      type: itemId,
      pendingName: undefined,
      needsInput: false,
    };
    const pricing = await getItemPricingInfo(customerId, itemId);
    if (!pricing) return nextBase;

    const currentRate = Number(line.rate) || 0;
    const effectiveRate = pricing.rate;
    const shouldPrompt =
      currentRate > 0 &&
      pricing.hasCustomerOverride &&
      !ratesEqual(currentRate, effectiveRate);

    if (currentRate <= 0) {
      return { ...nextBase, rate: effectiveRate, isTaxable: pricing.isTaxable };
    }

    if (!shouldPrompt) {
      return { ...nextBase, rate: currentRate, isTaxable: pricing.isTaxable };
    }

    const choice = await promptForRateConflict({
      itemName: pricing.itemName,
      currentRate,
      effectiveRate,
      ...promptContext,
    });

    return {
      ...nextBase,
      rate: choice === 'use' ? effectiveRate : currentRate,
      isTaxable: pricing.isTaxable,
    };
  }, [getItemPricingInfo, promptForRateConflict, ratesEqual]);

  const buildBrushPromptContext = useCallback((row: SpreadsheetRow, dateKey: string, index: number) => ({
    customerName: row.customerLookupKey || row.customer || 'Unknown customer',
    serviceDate: dateKey.replace('date_', ''),
    lineNumber: index + 1,
  }), []);

  const resolveBrushServiceLine = useCallback(async (
    row: SpreadsheetRow,
    dateKey: string,
    line: ServiceEntry | undefined,
    index: number
  ): Promise<ServiceEntry | null> => {
    if (!selectedServiceType || !line || line.type) return null;
    const nextLine = await resolveAssignedServiceLine(
      line,
      row.customerId,
      selectedServiceType,
      buildBrushPromptContext(row, dateKey, index)
    );
    return didServiceLineChange(line, nextLine) ? nextLine : null;
  }, [buildBrushPromptContext, didServiceLineChange, resolveAssignedServiceLine, selectedServiceType]);

  const resolveAssignedServiceLineRef = useRef(resolveAssignedServiceLine);
  useEffect(() => {
    resolveAssignedServiceLineRef.current = resolveAssignedServiceLine;
  }, [resolveAssignedServiceLine]);

  useEffect(() => {
    setSpreadsheetRows((prev) => {
      let changed = false;
      const nextRows = prev.map((row) => {
        if (!isCustomerTaxExempt(row.customerId)) return row;
        let rowChanged = false;
        const nextRow: any = { ...row };
        Object.keys(nextRow).forEach((key) => {
          if (!key.startsWith('date_') || !Array.isArray(nextRow[key])) return;
          const nextServices = nextRow[key].map((service: ServiceEntry) => {
            if (!service?.isTaxable) return service;
            rowChanged = true;
            return { ...service, isTaxable: false };
          });
          nextRow[key] = nextServices;
        });
        if (rowChanged) {
          changed = true;
          return nextRow as SpreadsheetRow;
        }
        return row;
      });
      return changed ? nextRows : prev;
    });
  }, [isCustomerTaxExempt]);

  const didInitFromPersistRef = useRef(false);
  useEffect(() => {
    if (didInitFromPersistRef.current) return;
    if (initialSpreadsheetRows && initialSpreadsheetRows.length > 0) {
      didInitFromPersistRef.current = true;
      setSpreadsheetRows(initialSpreadsheetRows);
      // Build date columns from persisted rows for columns layout
      const dateSet = new Set<string>();
      initialSpreadsheetRows.forEach(r => Object.keys(r).forEach(k => { if (k.startsWith('date_')) dateSet.add(k.replace('date_', '')); }));
      const dates = Array.from(dateSet.values()).sort();
      setDateColumnsState(dates.map(d => ({ field: `date_${d}`, headerName: d })));
    }
  }, [initialSpreadsheetRows]);

  // Recompute from uploaded rows only when no persisted state
  useEffect(() => {
    if (initialSpreadsheetRows && initialSpreadsheetRows.length > 0) return;
    if (!rows || !mapping) return;

    if (containsDates) {
      const dateFields: string[] = Array.isArray(mapping?.dateColumns)
        ? mapping.dateColumns
        : (mapping?.date ? [mapping.date] : []);
      const dateValues = new Set<string>();
      rows.forEach(row => {
        dateFields.forEach(df => {
          const v = row?.[df];
          if (v) {
            // Accept raw date or semicolon segments with leading date
            const parsed = parseSemicolonCell(String(v));
            if (parsed.length) {
              parsed.forEach(seg => { if (seg.date) dateValues.add(seg.date); });
            } else {
              const d = parseFlexibleDate(String(v));
              if (d) dateValues.add(d);
            }
          }
        });
      });
      const toTime = (s: string) => {
        const d = new Date(s);
        return isNaN(d.getTime()) ? Number.MAX_SAFE_INTEGER : d.getTime();
      };
      const uniqueDates = Array.from(dateValues).sort((a,b) => toTime(a) - toTime(b));
      // Build stable date columns state
      const dateCols = uniqueDates.map(d => ({ field: getDateColId(d), headerName: d }));
      // Avoid unnecessary state updates that can cause AG Grid to constantly recreate cells
      setDateColumnsState(prev => {
        if (prev.length === dateCols.length && prev.every((c, idx) => c.field === dateCols[idx].field && c.headerName === dateCols[idx].headerName)) {
          return prev;
        }
        return dateCols;
      });
      // Allow blank customer rows to appear in the grid
      const uniqueCustomers = [...new Set(rows.map(row => row[mapping.customer]))];

      const customerDateMap = new Map<string, Map<string, ServiceEntry[]>>();

      rows.forEach(row => {
        const customer = row[mapping.customer] ?? '';
        const defaultQty = parseFloat(row[mapping.quantity]) || 1;
        const explicitRate = parseFloat(row[mapping.rate]);
        dateFields.forEach(df => {
          const raw = row[df];
          if (!raw) return;
          const segments = parseSemicolonCell(String(raw));
          const fallbacks = segments.length ? segments : [{ date: parseFlexibleDate(String(raw)) || String(raw), qty: defaultQty, price: isNaN(explicitRate) ? undefined : explicitRate }];
          for (const seg of fallbacks) {
            const normalizedDate = seg.date || parseFlexibleDate(String(raw)) || String(raw);
            if (!normalizedDate) continue;
            if (!customerDateMap.has(customer)) customerDateMap.set(customer, new Map());
            if (!customerDateMap.get(customer)!.has(normalizedDate)) customerDateMap.get(customer)!.set(normalizedDate, []);
                          const baseEntry: ServiceEntry = {
                type: '',
                qty: seg.qty || defaultQty || 1,
                rate: typeof seg.price === 'number' ? seg.price : (isNaN(explicitRate) ? 0 : explicitRate),
                isTaxable: true,
              };
              // Forced item via !NAME!
              if (seg.forcedItemLabel && seg.forcedItemLabel.trim()) {
              const forced = seg.forcedItemLabel.trim();
              const quotedDescription = extractQuotedDescription(seg.label);
              const match = (items || []).find((i:any) => (i.name || '').toLowerCase() === forced.toLowerCase());
                              if (match) {
                  const entryResolved: ServiceEntry = { ...baseEntry, type: match.id, pendingName: forced, forcedItemLabel: forced, description: quotedDescription, _key: generateKey() };
                  customerDateMap.get(customer)!.get(normalizedDate)!.push(entryResolved);
                  console.debug('[csv] forced match entry', { customer, normalizedDate, entry: entryResolved });
                  continue;
                } else {
                  const entryForcedPending: ServiceEntry = { ...baseEntry, pendingName: forced, forcedItemLabel: forced, description: quotedDescription, needsInput: true, _key: generateKey() };
                  customerDateMap.get(customer)!.get(normalizedDate)!.push(entryForcedPending);
                  console.debug('[csv] forced pending entry', { customer, normalizedDate, entry: entryForcedPending });
                  continue;
                }
              }
              if (seg.label && seg.label.trim()) {
              const lbl = seg.label.trim();
              const quotedDescription = extractQuotedDescription(lbl);
              if (quotedDescription !== undefined) {
                const entryWithDesc: ServiceEntry = { ...baseEntry, description: quotedDescription, needsInput: true, _key: generateKey() };
                customerDateMap.get(customer)!.get(normalizedDate)!.push(entryWithDesc);
                console.debug('[csv] quoted description entry', { customer, normalizedDate, entry: entryWithDesc });
                continue;
              }
              if (!seg.forcedItemLabel) {
                // Label provided → attempt to resolve later in UI; mark placeholder
                const entryWithKey: ServiceEntry = { ...baseEntry, pendingName: lbl, needsInput: true, _key: generateKey() };
                customerDateMap.get(customer)!.get(normalizedDate)!.push(entryWithKey);
                console.debug('[csv] label placeholder entry', { customer, normalizedDate, entry: entryWithKey });
                continue;
              }
            } else if (typeof seg.price === 'number' && !seg.forcedItemLabel) {
              // Price-only with no label: two entries per rule (base service + priced entry needs input)
              customerDateMap.get(customer)!.get(normalizedDate)!.push({ ...baseEntry, pendingName: DEFAULT_BASE_ITEM_NAME, needsInput: true, _key: generateKey() });
              // Ensure the priced entry is also marked as needing input
              const priced = { ...baseEntry, needsInput: true, _key: generateKey() } as ServiceEntry;
              customerDateMap.get(customer)!.get(normalizedDate)!.push(priced);
              console.debug('[csv] price-only fallback entries', { customer, normalizedDate, base: DEFAULT_BASE_ITEM_NAME, priced });
              continue;
            }
            const baseNoLabel: ServiceEntry = { ...baseEntry, _key: generateKey() };
            customerDateMap.get(customer)!.get(normalizedDate)!.push(baseNoLabel);
            console.debug('[csv] base entry no label/price', { customer, normalizedDate, entry: baseNoLabel });
          }
        });
      });

      const spreadsheetData: SpreadsheetRow[] = uniqueCustomers.map((customer, idx) => {
        const row: SpreadsheetRow = { rowId: `${customer || 'row'}#${idx}`, customer: customer ?? '', customerId: null, customerLookupKey: customer ?? '' } as SpreadsheetRow;
        // Best-effort initial match using currently loaded customers (may be empty on first mount)
        const matchingCustomer = customers?.find(c => (c.display_name?.toLowerCase() || '').includes((customer || '').toLowerCase()) || (c.billing_addr_line1?.toLowerCase() || '').includes((customer || '').toLowerCase()));
        row.customerId = matchingCustomer?.id || null;
        uniqueDates.forEach(date => {
          const dateKey = getDateColId(date);
          const services = customerDateMap.get(customer)?.get(date) || [];
          // Only attach date field if this row actually has services for that date
          if (services.length > 0) {
            (row as any)[dateKey] = services;
          }
        });
        return row;
      });

      const fallbackKey = `date_${new Date().toISOString().slice(0,10)}`;
      const nextRows: SpreadsheetRow[] = spreadsheetData.length ? spreadsheetData : [{ rowId: fallbackRowIdRef.current, customer: '', customerId: null, customerLookupKey: '', [fallbackKey]: [] } as SpreadsheetRow];

      // Avoid unnecessary state churn that can trigger deep render loops in AG Grid
      setSpreadsheetRows(prev => {
        try {
          const a = JSON.stringify(prev);
          const b = JSON.stringify(nextRows);
          return a === b ? prev as SpreadsheetRow[] : nextRows;
        } catch {
          return nextRows;
        }
      });
    } else {
      // No-dates mode: Build one month column using monthStart and count service cells
      const monthIso = monthStart || new Date().toISOString().slice(0,10).slice(0,7) + '-01';
      const monthKey = getDateColId(monthIso);
      setDateColumnsState([{ field: monthKey, headerName: monthIso }]);
      const uniqueCustomers = [...new Set(rows.map(row => row[mapping.customer]))];
      const spreadsheetData: SpreadsheetRow[] = uniqueCustomers.map((customer, idx) => {
        const row: SpreadsheetRow = { rowId: `${customer || 'row'}#${idx}`, customer: customer ?? '', customerId: null, customerLookupKey: customer ?? '' } as SpreadsheetRow;
        const matchingCustomer = customers?.find(c => (c.display_name?.toLowerCase() || '').includes((customer || '').toLowerCase()) || (c.billing_addr_line1?.toLowerCase() || '').includes((customer || '').toLowerCase()));
        row.customerId = matchingCustomer?.id || null;
        // Collect service-type strings across mapped service columns
        const serviceColumns: string[] = Array.isArray(mapping?.serviceColumns) ? mapping.serviceColumns : (
          mapping?.service ? [mapping.service] : []
        );

        // Build services for this customer's rows using shared fold util
        const { buildNoDatesServicesForCustomer } = require('./noDatesFold');
        const customerRows = rows.filter((r: any) => (r[mapping.customer] ?? '') === (customer ?? ''));
        const services: ServiceEntry[] = buildNoDatesServicesForCustomer(customerRows, serviceColumns, monthIso, parseSemicolonCell, generateKey) as ServiceEntry[];
        (row as any)[monthKey] = services;
        return row;
      });
      const nextRows: SpreadsheetRow[] = spreadsheetData.length ? spreadsheetData : [{ rowId: fallbackRowIdRef.current, customer: '', customerId: null, customerLookupKey: '', [monthKey]: [] } as SpreadsheetRow];
      setSpreadsheetRows(nextRows);
    }
  }, [rows, mapping, getDateColId, parseFlexibleDate, parseSemicolonCell, customers, initialSpreadsheetRows, containsDates, monthStart]);

  useEffect(() => {
    let cancelled = false;

    const resolveForcedServiceAssignments = async () => {
      type ForcedResolution = {
        rowId: string;
        dateKey: string;
        serviceIndex: number;
        serviceKey?: string;
        line: ServiceEntry;
      };

      const changes: ForcedResolution[] = [];

      for (const row of spreadsheetRows) {
        if (!row.customerId) continue;

        for (const dateKey of Object.keys(row)) {
          if (!dateKey.startsWith('date_') || !Array.isArray((row as any)[dateKey])) continue;

          const services = (row as any)[dateKey] as ServiceEntry[];
          for (let serviceIndex = 0; serviceIndex < services.length; serviceIndex++) {
            const service = services[serviceIndex];
            const forced = service?.forcedItemLabel || (service?.type && service?.pendingName ? service.pendingName : '');
            if (!forced) continue;

            const match = service.type
              ? { id: service.type }
              : (items || []).find((item: any) => (item.name || '').toLowerCase() === forced.toLowerCase());
            if (!match?.id) continue;

            const resolved = await resolveAssignedServiceLineRef.current(
              service,
              row.customerId,
              match.id,
              {
                customerName: row.customerLookupKey || row.customer || 'Unknown customer',
                serviceDate: dateKey.replace('date_', ''),
                lineNumber: serviceIndex + 1,
              }
            );
            changes.push({
              rowId: row.rowId,
              dateKey,
              serviceIndex,
              serviceKey: service._key,
              line: { ...resolved, forcedItemLabel: undefined },
            });
          }
        }
      }

      if (cancelled || changes.length === 0) return;

      setSpreadsheetRows(prev => {
        let changed = false;
        const nextRows = prev.map(row => {
          const rowChanges = changes.filter(change => change.rowId === row.rowId);
          if (!rowChanges.length) return row;

          let rowChanged = false;
          const nextRow: any = { ...row };
          for (const change of rowChanges) {
            const currentServices = nextRow[change.dateKey];
            if (!Array.isArray(currentServices)) continue;

            const current = currentServices[change.serviceIndex];
            if (!current || (change.serviceKey && current._key !== change.serviceKey)) continue;
            if (!current.forcedItemLabel && !(current.type && current.pendingName)) continue;
            if (!didServiceLineChange(current, change.line)) continue;

            const nextServices = [...currentServices];
            nextServices[change.serviceIndex] = change.line;
            nextRow[change.dateKey] = nextServices;
            rowChanged = true;
          }

          if (!rowChanged) return row;
          changed = true;
          return nextRow as SpreadsheetRow;
        });

        if (changed) spreadsheetRowsRef.current = nextRows;
        return changed ? nextRows : prev;
      });
    };

    void resolveForcedServiceAssignments();

    return () => {
      cancelled = true;
    };
  }, [spreadsheetRows, items, didServiceLineChange]);

  // Do not emit on every keystroke to avoid parent re-renders causing focus loss
  // We will emit a snapshot when continuing to preview
  // (handled inside saveDrafts before calling onValidated)

  // Paint brush functionality
  const handlePaintStart = useCallback((serviceTypeId: string) => {
    setSelectedServiceType(serviceTypeId);
    setIsPainting(true);
  }, []);

  const applyBrushToCell = useCallback(async (row: SpreadsheetRow, dateKey: string) => {
    if (!isPainting || !selectedServiceType) return;
    const liveRow = spreadsheetRowsRef.current.find(r => r.rowId === row.rowId) || row;
    const currentServices = (liveRow as any)[dateKey] || [];
    if (!Array.isArray(currentServices) || currentServices.length === 0) {
      return; // do not create new entries via brush
    }
    const nextServices = [...currentServices];
    let changed = false;
    for (let index = 0; index < nextServices.length; index++) {
      const nextLine = await resolveBrushServiceLine(liveRow, dateKey, nextServices[index], index);
      if (!nextLine) continue;
      nextServices[index] = nextLine;
      changed = true;
    }
    if (!changed) return;
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(r => r.rowId === row.rowId ? { ...r, [dateKey]: nextServices } : r);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
  }, [isPainting, resolveBrushServiceLine, selectedServiceType]);

  const handlePaintEnd = useCallback(() => {
    setIsPainting(false);
    setIsMouseDown(false);
    lastPaintedKeyRef.current = null;
  }, []);

  const updateCell = useCallback((rowId: string, dateKey: string, services: ServiceEntry[]) => {
    // Non-structural update (preserve focus): no row height reset
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(row => row.rowId === rowId ? { ...row, [dateKey]: services } : row);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
  }, []);

  const updateCellStructural = useCallback((rowId: string, dateKey: string, services: ServiceEntry[]) => {
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(row => row.rowId === rowId ? { ...row, [dateKey]: services } : row);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
    resetRowHeights();
  }, []);

  const addService = useCallback((rowId: string, dateKey: string) => {
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(row => row.rowId === rowId ? { ...row, [dateKey]: [...(row[dateKey] || []), { type: '', qty: 1, rate: 0, isTaxable: true, description: '', _key: generateKey() }] } : row);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
    resetRowHeights();
  }, []);

  const removeService = useCallback((rowId: string, dateKey: string, serviceIndex: number) => {
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(row => row.rowId === rowId ? { ...row, [dateKey]: row[dateKey].filter((_: any, index: number) => index !== serviceIndex) } : row);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
    resetRowHeights();
  }, []);

  const confirmSplitCell = useCallback((text: string, delimiter: string = ';') => {
    if (!splitCellModal) return;
    const services = text.split(delimiter).map(() => ({ type: '', qty: 1, rate: 0, isTaxable: true }));
    updateCell(splitCellModal.rowId, splitCellModal.dateKey, services);
    setSplitCellModal(null);
  }, [splitCellModal, updateCell]);

  // Save drafts to backend
  const saveDrafts = useCallback(async () => {
    // Guard: require business selection
    if (!currentBusiness?.id) {
      alert('Please select a business before continuing.');
      return;
    }

    // Validate that all services have a service type assigned
    const validationErrors: { rowId: string; dateKey: string; customerName: string; serviceIndex: number }[] = [];
    
    spreadsheetRows.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key.startsWith('date_')) {
          const date = key.replace('date_', '');
          const services = row[key] || [];
          services.forEach((service: ServiceEntry, serviceIndex: number) => {
            if (!service.type || service.type.trim() === '') {
              validationErrors.push({
                rowId: row.rowId,
                dateKey: key,
                customerName: row.customer || row.customerLookupKey || 'Unknown Customer',
                serviceIndex: serviceIndex + 1
              });
            }
          });
        }
      });
    });

    // If validation fails, show error and prevent continuation
    if (validationErrors.length > 0) {
      const errorMessage = `Cannot continue: ${validationErrors.length} service(s) missing service type.\n\n` +
        validationErrors.slice(0, 5).map(err => 
          `• ${err.customerName} - ${err.dateKey.replace('date_', '')} (Service ${err.serviceIndex})`
        ).join('\n') +
        (validationErrors.length > 5 ? `\n... and ${validationErrors.length - 5} more` : '');
      
      alert(errorMessage);
      return;
    }

    // Persist current grid state upward so going back/forward keeps changes
    try { onSpreadsheetRowsChange && onSpreadsheetRowsChange(spreadsheetRows); } catch {}
    const flatDrafts: any[] = [];
    spreadsheetRows.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key.startsWith('date_')) {
          const date = key.replace('date_', '');
          const services = row[key] || [];
          services.forEach((service: ServiceEntry) => {
            if (service.type) {
              flatDrafts.push({
                customer_id: row.customerId,
                customer_lookup_key: row.customerLookupKey,
                service_date: date,
                service_type_id: service.type,
                qty: service.qty,
                unit_rate: service.rate,
                is_taxable: normalizeTaxable(row.customerId, service.isTaxable),
                raw_text: service.description || service.pendingName || ''
              });
            }
          });
        }
      });
    });

    if (flatDrafts.length === 0) {
      alert('There are no services to preview. Please add at least one service.');
      return;
    }

    try {
      // Clear previous drafts for this session so preview reflects only current grid
      await fetch(`${API_BASE_URL}/service-drafts/session?sessionId=${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          ...(isCookieMode || !token ? {} : { 'Authorization': `Bearer ${token}` })
        }
      });
      await fetch(`${API_BASE_URL}/service-drafts/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(isCookieMode || !token ? {} : { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ sessionId, drafts: flatDrafts, options: { containsDates, monthStart } })
      });
      onValidated();
    } catch (error) {
      console.error('Failed to save drafts:', error);
    }
  }, [spreadsheetRows, sessionId, onValidated, containsDates, monthStart]);

  // Derive unique date columns from current spreadsheet rows (supports adding new columns)
  const uniqueDates = useMemo(() => {
    const set = new Set<string>();
    spreadsheetRows.forEach(r => Object.keys(r).forEach(k => { if (k.startsWith('date_')) set.add(k.replace('date_', '')); }));
    return Array.from(set).sort();
  }, [spreadsheetRows]);

  const addRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    const newRow: SpreadsheetRow = { rowId: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`), customer: '', customerId: null, customerLookupKey: '' } as SpreadsheetRow;
    // In Services-in-cell, dates are independent per row: start with no dates
    if (layoutMode === 'columns') {
      const dates = uniqueDates.length ? uniqueDates : [today];
      dates.forEach(d => { (newRow as any)[`date_${d}`] = []; });
    }
    setSpreadsheetRows(prev => [...prev, newRow]);
  };

  const addDateColumn = () => {
    const input = window.prompt('Enter new date (flexible: 7/12, 7/12/25, 7-12, 7-12-25, or YYYY-MM-DD):', new Date().toISOString().slice(0,10));
    if (!input) return;
    const normalized = parseFlexibleDate(input);
    if (!normalized) return alert('Invalid date format. Try 7/12, 7/12/25, 7-12, 7-12-25, or YYYY-MM-DD.');
    const key = getDateColId(normalized);
    // Initialize each row with one empty service by default
    setSpreadsheetRows(prev => prev.map(r => ({ ...r, [key]: (r as any)[key] || [{ type: '', qty: 1, rate: 0, isTaxable: true, description: '', _key: generateKey() }] })));
    setDateColumnsState(prev => prev.some(c => c.field === key) ? prev : [...prev, { field: key, headerName: normalized }]);
    resetRowHeights();
  };

  const removeDateColumn = (field: string) => {
    if (!field?.startsWith('date_')) return;
    if (!window.confirm('Remove this date column from all rows?')) return;
    setSpreadsheetRows(prev => prev.map(r => {
      const copy: any = { ...r };
      delete copy[field];
      return copy;
    }));
    setDateColumnsState(prev => prev.filter(c => c.field !== field));
    resetRowHeights();
  };

  // Remove a date only for a specific row (Services-in-cell independence)
  const removeDateForRow = useCallback((rowId: string, field: string) => {
    if (!field?.startsWith('date_')) return;
    setSpreadsheetRows(prev => prev.map(r => {
      if (r.rowId !== rowId) return r;
      const copy: any = { ...r };
      delete copy[field];
      return copy as SpreadsheetRow;
    }));
    // Clear collapsed state for that row/date
    const map = collapsedPanelsRef.current;
    const rowState = { ...(map.get(rowId) || {}) };
    if (rowState[field]) delete rowState[field];
    map.set(rowId, rowState);
    resetRowHeights();
  }, []);

  const deleteRow = (rowId: string) => {
    setSpreadsheetRows(prev => prev.filter(r => r.rowId !== rowId));
  };

  // Ref indirection for brush-apply-on-column to avoid ordering issues
  const applyBrushToDateColumnRef = useRef<(dateField: string) => void>(() => {});

  // Define columnDefs after helpers so references are declared
  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    // Inline local components so TypeScript finds them in this scope
    const CustomerRenderer: React.FC<any> = (props) => {
      const row: SpreadsheetRow = props.data;
      const hasError = !row.customerId;
      const inputRef = useRef<HTMLInputElement>(null);
      const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number; visible: boolean }>({ top: 0, left: 0, width: 0, visible: false });
      const [options, setOptions] = useState<any[]>([]);
      const updateDropdownPosition = useCallback(() => {
        const el = inputRef.current; if (!el) return; const rect = el.getBoundingClientRect();
        setDropdownPos(prev => { const next = { top: rect.bottom + 4, left: rect.left, width: rect.width, visible: prev.visible }; return (prev.top===next.top&&prev.left===next.left&&prev.width===next.width&&prev.visible===next.visible)?prev:next; });
      }, []);
      useEffect(() => { if (!dropdownPos.visible) return; const handler = () => updateDropdownPosition(); window.addEventListener('scroll', handler, true); window.addEventListener('resize', handler); return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); }; }, [dropdownPos.visible, updateDropdownPosition]);
      return (
        <div className="relative" style={{ padding: 4 }}>
          <input
            type="text"
            ref={inputRef}
            defaultValue={row.customerLookupKey || ''}
            onFocus={() => updateDropdownPosition()}
            onChange={async (e) => {
              const qRaw = e.target.value; if ((qRaw||'').trim().length < 2) { setOptions([]); setDropdownPos(p=>({...p, visible:false})); return; }
              try {
                const data = await api.get<any[]>(`/customers/search?q=${encodeURIComponent(qRaw)}`);
                setOptions(Array.isArray(data) ? data : []);
                setDropdownPos(p=>({...p, visible:true}));
              } catch {
                const local = (customers || []).filter((c: any) => {
                  const needle = qRaw.toLowerCase();
                  return (c.display_name?.toLowerCase() || '').includes(needle) || (c.billing_addr_line1?.toLowerCase() || '').includes(needle);
                });
                setOptions(local);
                setDropdownPos(p=>({...p, visible:true}));
              }
              updateDropdownPosition();
            }}
            onBlur={() => { const txt = inputRef.current?.value ?? ''; setSpreadsheetRows(prev => prev.map(r => r.rowId === row.rowId ? { ...r, customerLookupKey: txt } : r)); setDropdownPos(p=>({...p, visible:false})); }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`w-full rounded-xl border ${hasError ? 'bg-red-50 border-red-300' : 'border-gray-300 bg-white'} px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white`}
          />
          {dropdownPos.visible && createPortal(
            <ul role="listbox" style={{ position:'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex:10000 }} className="bg-white border rounded shadow max-h-60 overflow-y-auto">
              {options.length === 0 ? (
                <li className="px-2 py-2 text-sm text-gray-500 select-none">No matches</li>
              ) : (
                options.map((c: any) => (
                  <li key={c.id} className="px-2 py-1 hover:bg-gray-100 cursor-pointer text-sm" onMouseDown={(e)=>{ e.preventDefault(); const display = `${c.display_name}${c.billing_addr_line1 ? ' — ' + c.billing_addr_line1 : ''}`; if (inputRef.current) inputRef.current.value = display; setSpreadsheetRows(prev => prev.map(r => r.rowId === row.rowId ? { ...r, customerId: c.id, customerLookupKey: display } : r)); setOptions([]); setDropdownPos(p=>({...p, visible:false})); }}>{c.display_name} — {c.billing_addr_line1 || ''}</li>
                ))
              )}
            </ul>,
            document.body
          )}
        </div>
      );
    };

    const ServiceCell: React.FC<any> = (props) => {
      const row: SpreadsheetRow = props.data;
      const dateKey: string = props.colDef.field!;
      const services: ServiceEntry[] = row[dateKey] || [];
      const rowTaxExempt = isCustomerTaxExempt(row.customerId);
      const availableItems: any[] = useMemo(() => items || [], [items]);
      const [activeIndex, setActiveIndex] = useState<number>(-1);
      const [queryByIndex, setQueryByIndex] = useState<Record<number, string>>(() => {
        const next: Record<number, string> = {};
        services.forEach((service, index) => {
          const cacheKey = getServiceEditorKey(row.rowId, dateKey, service, index);
          const cached = serviceEditorQueryCacheRef.current[cacheKey];
          if (typeof cached === 'string') next[index] = cached;
        });
        return next;
      });
      const [show, setShow] = useState(false);
      const anchorRefs = useRef<Record<number, HTMLInputElement | null>>({});
      const [options, setOptions] = useState<any[]>([]);
      const openItemCreate = (prefill: string) => { setItemDrawerInitial(prefill); setIsItemDrawerOpen(true); };
      const getLineKey = useCallback((service: ServiceEntry | undefined, index: number) => {
        return getServiceEditorKey(row.rowId, dateKey, service, index);
      }, [dateKey, row.rowId]);
      const resolvedName = useCallback((svc: ServiceEntry): string => {
        const found = availableItems.find((i:any) => i.id === svc.type);
        if (!found && !svc.pendingName) {
          console.debug('[render] blank name', { type: svc.type, pendingName: svc.pendingName, itemsLen: availableItems.length });
        }
        return found ? found.name : (svc.pendingName || '');
      }, [availableItems]);
      const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
        const txt = e.clipboardData.getData('text');
        console.debug('[paste] invoked', { txt });
        if (!txt) { console.debug('[paste] no text'); return; }
        if (txt.indexOf(';') === -1) { console.debug('[paste] no semicolon', { txt }); return; }
        e.preventDefault();
        const inheritDate = dateKey.replace('date_', ''); const segs = parseSemicolonCell(txt, inheritDate); console.debug('[paste] parsed segments', segs); if (!segs.length) return;
                  const built: ServiceEntry[] = [];
          for (const seg of segs) {
            let entry: ServiceEntry = { type: '', qty: seg.qty || 1, rate: seg.price || 0, isTaxable: true, _key: generateKey() };
            // 1) Forced item via !NAME!
            if (seg.forcedItemLabel) {
            const forced = seg.forcedItemLabel.trim();
            const match = availableItems.find((i:any) => (i.name || '').toLowerCase() === forced.toLowerCase());
            if (match) {
              entry = await resolveAssignedServiceLine({ ...entry, pendingName: forced }, row.customerId, match.id);
            } else {
              entry.pendingName = forced;
              entry.forcedItemLabel = forced;
              entry.description = extractQuotedDescription(seg.label) || entry.description;
              entry.needsInput = true;
              // Do not treat remaining label when forced item is present
              built.push(entry);
              continue;
            }
          }
          // 2) Remaining label may be a quoted description
          if (seg.label && seg.label.trim()) {
            const lbl = seg.label.trim();
            const quotedDescription = extractQuotedDescription(lbl);
            if (quotedDescription !== undefined) {
              entry.description = quotedDescription;
              entry.needsInput = entry.needsInput ?? true; // keep flagged if not resolved
            } else if (!entry.type && !seg.forcedItemLabel) {
              // Only treat as pendingName when we didn't already force an item
              entry.pendingName = lbl;
              entry.needsInput = true;
            }
          } else if (!entry.type && typeof seg.price === 'number' && !seg.forcedItemLabel) {
            built.push({ type: '', qty: 1, rate: 0, isTaxable: true, pendingName: DEFAULT_BASE_ITEM_NAME, needsInput: true, _key: generateKey() });
            entry.needsInput = true;
          }
          built.push(entry);
        }
        console.debug('[paste] built entries:', built.map(b => ({ type: b.type, pendingName: b.pendingName, needsInput: b.needsInput, rate: b.rate })));
        setSpreadsheetRows(prev => {
          const next = prev.map(r => r.rowId === row.rowId ? { ...r, [dateKey]: ([...(r as any)[dateKey] || [], ...built]) } as any : r);
          // snapshot of the updated date cell for this row
          console.debug('[paste] stored row snapshot', row.rowId, (next.find(r => r.rowId === row.rowId) as any)?.[dateKey]);
          return next;
        });
        resetRowHeights();
      };
      return (
        <div className={`${isPainting && selectedServiceType ? 'bg-blue-50' : ''}`} style={{ padding: 6, minHeight: 60 }} onPaste={handlePaste}>
          {services.length > 0 ? (
            <div className="space-y-1">
              {services.map((service, index) => (
                <div key={service._key || index} className="flex gap-1 text-sm">
                  {(() => { if ((service.type && service.type.trim() !== '') || service.pendingName) { try { console.debug('[render] resolved name', { index, type: service.type, pendingName: service.pendingName, resolved: resolvedName(service) }); } catch {} } return null; })()}
                  <div className="relative flex-1 min-w-[160px] md:min-w-[200px]">
                    <input
                      data-service-editor
                      ref={(el) => { anchorRefs.current[index] = el; }}
                      type="text"
                      value={(() => { const override = queryByIndex[index]; const val = override ?? resolvedName(service); if (override === '' || (!override && !val)) { console.debug('[render] input value blank', { index, override, resolved: resolvedName(service), svc: service }); } return val; })()}
                      placeholder="Search or create item"
                      className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                      onFocus={() => { setActiveIndex(index); }}
                      onChange={async (e) => {
                        const q = e.target.value;
                        const lineKey = getLineKey(services[index], index);
                        serviceEditorQueryCacheRef.current[lineKey] = q;
                        setQueryByIndex(prev => ({ ...prev, [index]: q }));
                        const list = await fetchCustomerItems(row.customerId || '');
                        const all = list.length ? list : availableItems;
                        const filtered = q.trim().length < 1 ? all : all.filter((i:any) => (i.name||'').toLowerCase().includes(q.toLowerCase()));
                        setOptions(filtered.slice(0, 30));
                        setShow(true);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (options.length > 0) {
                            const opt = options[0];
                            const next = [...services];
                            next[index] = await resolveAssignedServiceLine(next[index], row.customerId, opt.id);
                            serviceEditorQueryCacheRef.current[getLineKey(next[index], index)] = opt.name;
                            updateCell(row.rowId, dateKey, next);
                            setQueryByIndex(prev => ({ ...prev, [index]: opt.name }));
                            setShow(false);
                          } else if ((queryByIndex[index] || '').trim()) {
                            openItemCreate((queryByIndex[index] || '').trim());
                          }
                        }
                      }}
                      onBlur={() => setTimeout(()=>setShow(false), 150)}
                    />
                  </div>
                  <input type="number" value={services[index].qty} onChange={(e)=>{ const val = parseFloat(e.target.value) || 0; if (val < 0) return; const next = [...services]; next[index].qty = val; updateCell(row.rowId, dateKey, next); }} className="w-14 rounded-xl border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                  <input type="number" value={services[index].rate} onChange={(e)=>{ const val = parseFloat(e.target.value) || 0; if (val < 0) return; const next = [...services]; next[index].rate = val; updateCell(row.rowId, dateKey, next); }} className="w-16 rounded-xl border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                  <input type="text" value={services[index].description || ''} onChange={(e)=>{ const next = [...services]; next[index].description = e.target.value; updateCell(row.rowId, dateKey, next); }} placeholder="Description" className="flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                  <button onClick={() => removeService(row.rowId, dateKey, index)} className="text-red-500 hover:text-red-700 text-xs">×</button>
                  {services[index].needsInput && (
                    <>
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">Needs input</span>
                      <button className="ml-1 text-xs text-amber-700 underline" onClick={() => { pendingPlaceholderRef.current = { rowId: row.rowId, dateKey, index }; openItemCreate(services[index].pendingName || ''); }}>Create item</button>
                    </>
                  )}
                  {rowTaxExempt && (
                    <span
                      className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600"
                      title="Client is tax-exempt."
                    >
                      Tax exempt
                    </span>
                  )}
                  {(!services[index].type || services[index].type.trim() === '') && (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-red-100 text-red-800">Missing service type</span>
                  )}
                </div>
              ))}
              <button onClick={() => addService(row.rowId, dateKey)} className="text-blue-600 hover:text-blue-800 text-xs">+ Add</button>
            </div>
          ) : (
            <button onClick={() => addService(row.rowId, dateKey)} className="text-gray-500 hover:text-gray-700 text-xs">Add Service</button>
          )}
          {show && activeIndex >= 0 && createPortal(
            <div style={{ position:'fixed', top: (anchorRefs.current[activeIndex]?.getBoundingClientRect().bottom||0)+4, left: anchorRefs.current[activeIndex]?.getBoundingClientRect().left||0, width: anchorRefs.current[activeIndex]?.offsetWidth||240, zIndex: 10000 }} className="bg-white border rounded shadow max-h-60 overflow-y-auto">
              {options.length === 0 ? (
                <div className="px-2 py-2 text-xs text-gray-600">
                  No matches
                  <button className="ml-2 text-blue-600 underline" onMouseDown={(e)=>{ e.preventDefault(); openItemCreate((queryByIndex[activeIndex]||'').trim()); }}>Add "{(queryByIndex[activeIndex]||'').trim()}"</button>
                </div>
              ) : (
                <ul>
                  {options.map((opt:any) => (
                    <li key={opt.id} className="px-2 py-1 hover:bg-gray-100 cursor-pointer text-xs" onMouseDown={async (e)=>{
                      e.preventDefault();
                      const targetIndex = activeIndex;
                      if (targetIndex < 0 || !services[targetIndex]) return;
                      const next = [...services];
                      next[targetIndex] = await resolveAssignedServiceLine(next[targetIndex], row.customerId, opt.id);
                      serviceEditorQueryCacheRef.current[getLineKey(next[targetIndex], targetIndex)] = opt.name;
                      updateCell(row.rowId, dateKey, next);
                      setQueryByIndex(prev => ({ ...prev, [targetIndex]: opt.name }));
                      setShow(false);
                    }}>
                      {opt.name}
                    </li>
                  ))}
                  <li className="px-2 py-1 text-blue-600 hover:bg-blue-50 cursor-pointer text-xs" onMouseDown={(e)=>{ e.preventDefault(); openItemCreate((queryByIndex[activeIndex]||'').trim()); }}>+ Create new item "{(queryByIndex[activeIndex]||'').trim()}"</li>
                </ul>
              )}
            </div>,
            document.body
          )}
        </div>
      );
    };

    // Custom header for date columns to support brush-apply-on-click
    const DateHeaderRenderer: React.FC<any> = (p) => {
      const field: string = p.column?.getColId?.() || p?.displayName || p?.colDef?.field;
      const name = (p?.colDef?.headerName || '').toString();
      const handleApply = (e: React.MouseEvent) => {
        if (isPainting && selectedServiceType && field && String(field).startsWith('date_')) {
          e.preventDefault();
          e.stopPropagation();
          applyBrushToDateColumnRef.current && applyBrushToDateColumnRef.current(field);
        }
      };
      return (
        <div
          className="flex items-center justify-between w-full"
          onMouseDown={handleApply}
          onClick={handleApply}
        >
          <span title={isPainting ? 'Click to apply brush to this column' : name}>{name}</span>
          {isPainting && <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-blue-100 text-blue-700">Brush</span>}
        </div>
      );
    };

    // Services-in-cell renderer for a specific slot index
    const ServicesCellRenderer: React.FC<any> = (p) => {
      const row: SpreadsheetRow = p.data;
      const slotIndex: number = (p.colDef as any)?.cellRendererParams?.slotIndex ?? 0;
      const sorted = getRowDateKeysSorted(row);
      const dk = sorted[slotIndex];
      const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>(() => collapsedPanelsRef.current.get(row.rowId) || {});
      React.useEffect(() => {
        collapsedPanelsRef.current.set(row.rowId, collapsed);
      }, [row.rowId, collapsed]);
      if (!dk) {
        return <div className="p-2 text-xs text-gray-500" />;
      }
      const isCollapsed = !!collapsed[dk];
      const ht = highlightTargetRef.current;
      const selected = selectedCellsRef.current.includes(makeKey(row.rowId, dk));
      return (
        <div
          className={`h-full w-full border border-gray-200 rounded-xl p-2 ${selected ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-blue-50'} ${ht && ht.rowId===row.rowId && ht.dateKey===dk ? 'ring-2 ring-amber-400' : ''}`}
          onClick={(ev) => {
            if (isFormControlTarget(ev.target)) return;
            const key = makeKey(row.rowId, dk);
            setSelectedCells(prev => ev.shiftKey ? (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]) : [key]);
            setSelectedRowAnchor(row.rowId);
          }}
        >
          <div className="flex justify-between items-center mb-1">
            <button className="text-xs font-medium" onClick={()=>{ setCollapsed(prev=>{ const next={...prev,[dk]:!isCollapsed}; collapsedPanelsRef.current.set(row.rowId,next); resetRowHeights(); return next; });}}>
              {isCollapsed ? '▸' : '▾'} {dk.replace('date_','')}
            </button>
            <button className="text-xs text-red-600 hover:text-red-700 hover:underline" onClick={()=> removeDateForRow(row.rowId, dk)}>Remove date</button>
          </div>
          {!isCollapsed && React.createElement(ServiceCell as any, { data: row, colDef: { field: dk } })}
        </div>
      );
    };

    // Per-row add-date input used in Services-in-cell layout
    const AddDateCellRenderer: React.FC<any> = (p) => {
      const row: SpreadsheetRow = p.data;
      const isAnchor = selectedRowAnchorRef.current === row.rowId;
      return (
        <div
          data-add-date-cell
          className={`flex items-center justify-center ${isAnchor ? 'ring-2 ring-indigo-500 rounded' : ''}`}
          onClick={() => { setSelectedRowAnchor(row.rowId); setSelectedCells([]); }}
        >
          <input
            type="text"
            placeholder="Add date (flexible)"
            className="rounded-xl border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
            onKeyDown={(e:any)=>{
              if (e.key==='Enter') {
                const raw = String(e.currentTarget.value || '').trim();
                if (!raw) return;
                const parts = raw.split(/[;,]+/).map((s:string)=>s.trim()).filter(Boolean);
                if (parts.length === 0) return;
                const normals = parts.map((p:string)=>parseFlexibleDate(p)).filter(Boolean) as string[];
                if (normals.length === 0) return;
                setSpreadsheetRows(prev => prev.map(r => {
                  if (r.rowId !== row.rowId) return r;
                  const updated: any = { ...r };
                  for (const n of normals) {
                    const k = getDateColId(n);
                    if (!updated[k]) updated[k] = [];
                  }
                  return updated as SpreadsheetRow;
                }));
                (e.currentTarget as HTMLInputElement).value='';
                resetRowHeights();
              }
            }}
          />
        </div>
      );
    };

    const defs: ColDef[] = [
      { headerName: '', field: 'actions', pinned: 'left', width: 60, cellRenderer: (p:any) => (
        <button title="Delete row" onClick={() => deleteRow(p.data.rowId)} className="px-2 py-1 text-xs text-red-600 hover:text-red-800">Delete</button>
      )},
      { headerName: 'Customer', field: 'customer', pinned: 'left', flex: 1.5, minWidth: 360, cellRenderer: CustomerRenderer as any }
    ];
    if (layoutMode === 'columns') {
      dateColumnsState.forEach(({ field, headerName }) => {
        const disp = headerName.includes('-') ? headerName : headerName.replace(/_/g, '/');
        defs.push({ headerName: disp + '  ✕', field, flex: 2.5, minWidth: 420, cellRenderer: ServiceCell as any, valueFormatter: () => '', sortable: !isPainting, headerComponent: DateHeaderRenderer as any } as any);
      });
    } else {
      defs.push({ headerName: 'Add date', field: 'add_date', pinned: 'left', width: 180, cellRenderer: AddDateCellRenderer as any, cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' } as any });
      for (let i = 0; i < cellSlotsCount; i++) {
        defs.push({ headerName: `Services ${i+1}`, field: `services_slot_${i+1}`, flex: 2.2, minWidth: 460, cellRenderer: ServicesCellRenderer as any, cellRendererParams: { slotIndex: i }, valueFormatter: () => '' } as any);
      }
    }
    return defs as ColDef[];
  }, [dateColumnsState, layoutMode, parseFlexibleDate, getDateColId, cellSlotsCount, isPainting, selectedServiceType, getRowDateKeysSorted, resetRowHeights, items, fetchCustomerItems, resolveAssignedServiceLine, isFormControlTarget]);

  // Track last painted key to avoid repeated paints on same cell
  const lastPaintedKeyRef = useRef<string | null>(null);

  // Optional guard to reduce redundant paints on the same cell during drag
  const applyBrushWithGuard = useCallback((row: SpreadsheetRow, dateKey: string) => {
    const key = `${row.rowId}::${dateKey}`;
    if (lastPaintedKeyRef.current === key) return;
    lastPaintedKeyRef.current = key;
    void applyBrushToCell(row, dateKey);
  }, [applyBrushToCell]);

  useEffect(() => {
    if (!isPainting) lastPaintedKeyRef.current = null;
  }, [isPainting]);

  // Map event field to actual date key (handles Services-in-cell slots)
  const resolveEventDateKey = useCallback((row: SpreadsheetRow, e: any): string | null => {
    const field: string | undefined = e?.colDef?.field;
    if (!field) return null;
    if (field.startsWith('date_')) return field;
    if (field.startsWith('services_slot_')) {
      const slotIndex: number = (e.colDef as any)?.cellRendererParams?.slotIndex ?? -1;
      if (slotIndex < 0) return null;
      const sorted = getRowDateKeysSorted(row);
      return sorted[slotIndex] || null;
    }
    return null;
  }, [getRowDateKeysSorted]);

  // Apply brush selection to all empty services across the row
  const applyBrushToRow = useCallback(async (row: SpreadsheetRow) => {
    if (!isPainting || !selectedServiceType) return;
    const liveRow = spreadsheetRowsRef.current.find(r => r.rowId === row.rowId) || row;
    const copy: any = { ...liveRow };
    const dateKeys = Object.keys(copy).filter((k: string) => k.startsWith('date_'));
    let changed = false;
    for (const dk of dateKeys) {
      const list: ServiceEntry[] = Array.isArray(copy[dk]) ? [...copy[dk]] : [];
      if (list.length === 0) continue;
      for (let index = 0; index < list.length; index++) {
        const nextSvc = await resolveBrushServiceLine(liveRow, dk, list[index], index);
        if (!nextSvc) continue;
        list[index] = nextSvc;
        changed = true;
      }
      copy[dk] = list;
    }
    if (!changed) return;
    setSpreadsheetRows(prev => {
      const nextRows = prev.map(r => r.rowId === row.rowId ? copy as SpreadsheetRow : r);
      spreadsheetRowsRef.current = nextRows;
      return nextRows;
    });
  }, [isPainting, resolveBrushServiceLine, selectedServiceType]);

  // Apply brush to entire date column across all rows (columns layout)
  const applyBrushToDateColumn = useCallback(async (dateField: string) => {
    if (!isPainting || !selectedServiceType) return;
    const rowsSnapshot = spreadsheetRowsRef.current.slice();
    const nextRows = [] as SpreadsheetRow[];
    for (const row of rowsSnapshot) {
      const list: ServiceEntry[] = (row as any)[dateField] || [];
      if (!Array.isArray(list) || list.length === 0) {
        nextRows.push(row);
        continue;
      }
      const next = [...list];
      let changed = false;
      for (let index = 0; index < next.length; index++) {
        const nextLine = await resolveBrushServiceLine(row, dateField, next[index], index);
        if (!nextLine) continue;
        next[index] = nextLine;
        changed = true;
      }
      if (!changed) {
        nextRows.push(row);
        continue;
      }
      const copy: any = { ...row };
      copy[dateField] = next;
      nextRows.push(copy as SpreadsheetRow);
    }
    setSpreadsheetRows(nextRows);
    spreadsheetRowsRef.current = nextRows;
    resetRowHeights();
  }, [isPainting, resetRowHeights, resolveBrushServiceLine, selectedServiceType]);

  // Bind ref to the callback
  useEffect(() => {
    applyBrushToDateColumnRef.current = (df: string) => { void applyBrushToDateColumn(df); };
  }, [applyBrushToDateColumn]);

  // Click/drag painting: only when mouse is down and brush selected
  const onCellMouseOverDebounced = useCallback((e: CellMouseOverEvent) => {
    const row = e.data as SpreadsheetRow;
    const dk = resolveEventDateKey(row, e);
    if (!dk) return;
    if (!isMouseDown || !isPainting || !selectedServiceType) return;
    applyBrushWithGuard(row, dk);
  }, [isMouseDown, isPainting, selectedServiceType, applyBrushWithGuard, resolveEventDateKey]);

  // Release mouse down globally to avoid getting stuck
  useEffect(() => {
    const up = () => {
      setIsMouseDown(false);
      lastPaintedKeyRef.current = null;
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const hasMissingCustomers = spreadsheetRows.some(row => !row.customerId);
  let invalidQtyCount = 0;
  let missingServiceTypeCount = 0;
  for (const r of spreadsheetRows) {
    const keys = Object.keys(r).filter(k => k.startsWith('date_'));
    for (const k of keys) {
      const list: ServiceEntry[] = (r as any)[k] || [];
      invalidQtyCount += list.filter(s => (s.qty ?? 0) <= 0).length;
      missingServiceTypeCount += list.filter(s => !s.type || s.type.trim() === '').length;
    }
  }
  const hasInvalidRows = hasMissingCustomers || invalidQtyCount > 0 || missingServiceTypeCount > 0;
  const unresolvedCount = useMemo(() => {
    let n = 0;
    for (const r of spreadsheetRows) {
      const keys = Object.keys(r).filter(k => k.startsWith('date_'));
      for (const k of keys) {
        const list: ServiceEntry[] = (r as any)[k] || [];
        n += list.filter(s => s.needsInput).length;
      }
    }
    return n;
  }, [spreadsheetRows]);

  const goToFirstUnresolved = useCallback(() => {
    for (let i = 0; i < spreadsheetRows.length; i++) {
      const r = spreadsheetRows[i];
      const keys = Object.keys(r).filter(k => k.startsWith('date_'));
      for (const k of keys) {
        const list: ServiceEntry[] = (r as any)[k] || [];
        const idx = list.findIndex(s => s.needsInput);
        if (idx >= 0) {
          // Focus row and date cell in columns mode; highlight panel in cell mode
          if (layoutMode === 'columns') {
            gridRef.current?.api?.ensureIndexVisible(i, 'top');
            gridRef.current?.api?.setFocusedCell(i, k);
          } else {
            gridRef.current?.api?.ensureIndexVisible(i, 'top');
            setHighlightTarget({ rowId: r.rowId, dateKey: k });
            setTimeout(() => setHighlightTarget(null), 1500);
          }
          return;
        }
      }
    }
  }, [spreadsheetRows, layoutMode]);

  const goToFirstMissingServiceType = useCallback(() => {
    for (let i = 0; i < spreadsheetRows.length; i++) {
      const r = spreadsheetRows[i];
      const keys = Object.keys(r).filter(k => k.startsWith('date_'));
      for (const k of keys) {
        const list: ServiceEntry[] = (r as any)[k] || [];
        const idx = list.findIndex(s => !s.type || s.type.trim() === '');
        if (idx >= 0) {
          // Focus row and date cell in columns mode; highlight panel in cell mode
          if (layoutMode === 'columns') {
            gridRef.current?.api?.ensureIndexVisible(i, 'top');
            gridRef.current?.api?.setFocusedCell(i, k);
          } else {
            gridRef.current?.api?.ensureIndexVisible(i, 'top');
            setHighlightTarget({ rowId: r.rowId, dateKey: k });
            setTimeout(() => setHighlightTarget(null), 1500);
          }
          return;
        }
      }
    }
  }, [spreadsheetRows, layoutMode]);

  // Dynamic row height so inputs/services are not clipped
  const getRowHeight = useCallback((params: any) => {
    const row = params.data as SpreadsheetRow;
    if (!row) return 44;
    const dateKeys = Object.keys(row).filter(k => k.startsWith('date_'));
    const base = 96;
    const perService = 38;
    if (layoutMode === 'cell') {
      // Horizontal panels: take max panel height (collapsed vs expanded) instead of sum
      const header = 24; // date label and actions
      const panelPadding = 16; // 8 + 8
      const addDateBar = 40; // top input row
      const rowCollapsed = collapsedPanelsRef.current.get(row.rowId) || {};
      let maxPanel = 0;
      for (const k of dateKeys) {
        const len = Array.isArray((row as any)[k]) ? (row as any)[k].length : 0;
        const slots = Math.max(len, 1);
        const panelHeight = rowCollapsed[k] ? (panelPadding + header) : (panelPadding + header + slots * perService);
        if (panelHeight > maxPanel) maxPanel = panelHeight;
      }
      const total = 16 + addDateBar + maxPanel; // container padding + controls + tallest panel
      return Math.max(base, total);
    } else {
      // Columns mode: only the max services in any single date column determines height
      let maxServices = 0;
      for (const k of dateKeys) {
        const len = Array.isArray((row as any)[k]) ? (row as any)[k].length : 0;
        if (len > maxServices) maxServices = len;
      }
      return Math.max(base, base + (maxServices > 0 ? (maxServices - 1) * perService : 0));
    }
  }, [layoutMode]);

  // Keep a ref of selected cells for renderers and refresh grid on change
  const selectedCellsRef = useRef<string[]>([]);
  useEffect(() => {
    selectedCellsRef.current = selectedCells;
    refreshGridCells();
  }, [selectedCells, refreshGridCells]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // If click is outside grid cells and add-date cells, clear selection
      if (!target.closest('.ag-root') && !target.closest('[data-add-date-cell]')) {
        setSelectedCells([]);
      }
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8 overflow-visible">
      <h1 className="text-2xl font-bold mb-6">Review & Validate Data</h1>

      {!containsDates && (
        <div className="mb-4 p-3 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
          No-dates mode: All services will be billed for month starting {String(monthStart || '').slice(0,10) || new Date().toISOString().slice(0,7)+'-01'}. Edit service types, quantities, and rates below.
        </div>
      )}

      {/* Item Application Brush */}
      <div className="mb-4 p-4 rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800" onMouseDownCapture={() => setIsMouseDown(false)}>
        <h3 className="text-lg font-semibold mb-2">Item Application Brush</h3>
        <div className="flex gap-2 flex-wrap mb-2 items-center">
          <div className="flex items-center gap-1 mr-4 text-sm">
            <span className="text-gray-600">Zoom:</span>
            <button className="rounded-xl border px-2 py-1 text-sm bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra" onClick={zoomOut} title="Zoom out">−</button>
            <span className="px-2 w-14 text-center">{Math.round(zoom*100)}%</span>
            <button className="rounded-xl border px-2 py-1 text-sm bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra" onClick={zoomIn} title="Zoom in">+</button>
            <button className="rounded-xl border px-2 py-1 text-sm bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra ml-2" onClick={zoomReset} title="Reset zoom">Reset</button>
          </div>
          <select
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
            value={selectedServiceType || ''}
            onChange={(e) => {
              const val = (e.target as HTMLSelectElement).value || null;
              setSelectedServiceType(val);
            }}
            onFocus={() => setIsMouseDown(false)}
          >
            <option value="">Select item…</option>
            {items.map((item: any) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          <button
            onClick={() => setIsPainting(prev => !prev)}
            disabled={!selectedServiceType}
            className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra ${isPainting ? 'bg-sedra text-white hover:bg-sedra-700' : 'border border-sedra text-sedra hover:bg-sedra/5'} disabled:opacity-50`}
            title={!selectedServiceType ? 'Select an item first' : (isPainting ? 'Turn brush off' : 'Turn brush on')}
          >
            {isPainting ? 'Brush: On' : 'Brush: Off'}
          </button>
          <div className="ml-4 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Layout:</span>
            <button className={`rounded-xl px-2 py-1 border text-sm ${String(layoutMode)==='columns'?'bg-sedra text-white hover:bg-sedra-700 border-sedra':'bg-white hover:bg-gray-50'}`} onClick={()=> setLayoutMode('columns')}>Dates as columns</button>
            <button className={`rounded-xl px-2 py-1 border text-sm ${String(layoutMode)==='cell'?'bg-sedra text-white hover:bg-sedra-700 border-sedra':'bg-white hover:bg-gray-50'}`} onClick={()=> setLayoutMode('cell')}>Services in cell</button>
          </div>
          <div className="ml-4 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Base service:</span>
            <input className="w-[140px] rounded-xl border border-gray-300 bg-white px-2 py-1 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" value={baseServiceLabel} onChange={(e)=> setBaseServiceLabel(e.target.value)} />
          </div>
          {unresolvedCount > 0 && (
            <button className="ml-4 text-sm text-amber-700 underline" onClick={goToFirstUnresolved}>{unresolvedCount} item(s) need input — Jump</button>
          )}
          <span className="text-xs text-gray-500">Tip: Select an item, click Brush to toggle, then click and drag over existing service cells to apply.</span>
        </div>
        {isPainting && layoutMode==='columns' && dateColumnsState.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-gray-600">Apply brush to column:</span>
            {dateColumnsState.map(dc => (
              <button key={dc.field} className="rounded-xl border px-2 py-0.5 bg-white hover:bg-gray-50" onClick={()=>applyBrushToDateColumn(dc.field)}>
                {dc.headerName}
              </button>
            ))}
          </div>
        )}
        {/* Row/Column controls */}
        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={addRow} className="rounded-xl border px-3 py-1 text-sm bg-white hover:bg-gray-50">+ Add Row</button>
          {layoutMode==='columns' && (
            <button onClick={addDateColumn} className="rounded-xl border px-3 py-1 text-sm bg-white hover:bg-gray-50">+ Add Date Column</button>
          )}
          {layoutMode==='columns' && dateColumnsState.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <span>Remove date:</span>
              {dateColumnsState.map(dc => (
                <button key={dc.field} onClick={() => removeDateColumn(dc.field)} className="rounded-xl border px-2 py-0.5 bg-white hover:bg-gray-50">
                  {dc.headerName} ✕
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AG Grid */}
      <div className="ag-theme-alpine" style={{ width: layoutMode==='columns' ? '250%' : '100%', position: 'relative', left: layoutMode==='columns' ? '50%' : '0%', transform: layoutMode==='columns' ? 'translateX(-50%)' : 'none', height: '78vh', minHeight: '480px', zoom: zoom as any }}>
        {(() => {
          const defaultColDef = { resizable: true } as any;
          return (
            <AgGridReact
              ref={gridRef}
              rowData={spreadsheetRows}
              columnDefs={columnDefs}
              getRowId={(params: any) => params.data.rowId}
              defaultColDef={defaultColDef}
              suppressBrowserResizeObserver={true}
              onCellMouseOver={onCellMouseOverDebounced}
              getRowHeight={getRowHeight}
              onCellMouseDown={(e: CellMouseDownEvent) => {
                const row = e.data as SpreadsheetRow;
                const field = e.colDef.field as string | undefined;
                if (isPainting && selectedServiceType && field) {
                  // Ctrl+Shift+Click: apply brush to the entire date column corresponding to this cell.
                  if ((e.event as MouseEvent)?.ctrlKey && (e.event as MouseEvent)?.shiftKey) {
                    const dk = resolveEventDateKey(row, e);
                    if (dk && dk.startsWith('date_')) {
                      (e.event as MouseEvent).preventDefault();
                      (e.event as MouseEvent).stopPropagation();
                      applyBrushToDateColumnRef.current && applyBrushToDateColumnRef.current(dk);
                      return;
                    }
                  }
                  if (field === 'customer') {
                    // In brush mode, clicking the customer/address cell should fan out to the whole row.
                    (e.event as MouseEvent)?.preventDefault?.();
                    (e.event as MouseEvent)?.stopPropagation?.();
                    void applyBrushToRow(row);
                    return;
                  }
                  const dk = resolveEventDateKey(row, e);
                  if (dk) {
                    (e.event as MouseEvent)?.preventDefault?.();
                    (e.event as MouseEvent)?.stopPropagation?.();
                    lastPaintedKeyRef.current = null;
                    setIsMouseDown(true);
                    applyBrushWithGuard(row, dk);
                    return;
                  }
                }
                if (isFormControlTarget(e.event?.target)) return;
                setIsMouseDown(true);
              }}
            />
          );
        })()}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-between">
        <div className="text-sm text-gray-600">
          {hasInvalidRows && (
            <span className="text-red-600">
              ⚠️ {hasMissingCustomers ? 'Some customers need to be resolved' : ''}{hasMissingCustomers && (invalidQtyCount>0 || missingServiceTypeCount>0) ? ' · ' : ''}{invalidQtyCount>0 ? `${invalidQtyCount} item(s) have qty ≤ 0` : ''}{invalidQtyCount>0 && missingServiceTypeCount>0 ? ' · ' : ''}{missingServiceTypeCount>0 ? (
                <button 
                  onClick={goToFirstMissingServiceType}
                  className="text-red-600 underline hover:text-red-800 cursor-pointer"
                  title="Click to go to first missing service type"
                >
                  {missingServiceTypeCount} service(s) missing service type
                </button>
              ) : ''}
            </span>
          )}
        </div>
        <button onClick={saveDrafts} disabled={hasInvalidRows} className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-50 disabled:cursor-not-allowed">Continue to Preview</button>
      </div>

      {/* Overwrite Confirmation Modal */}
      {confirmOverwrite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="max-w-sm w-full rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">Confirm overwrite</h3>
            <p className="text-sm text-gray-700 mb-4">Overwrite {confirmOverwrite.count} non-empty cell(s)? This will replace existing services.</p>
            <div className="flex gap-2 justify-end">
              <button className="rounded-xl border px-3 py-1 text-sm bg-white hover:bg-gray-50" onClick={() => setConfirmOverwrite(null)}>Cancel</button>
              <button className="rounded-xl bg-sedra px-3 py-1 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra" onClick={() => { const fn = confirmOverwrite.proceed; setConfirmOverwrite(null); fn && fn(); }}>Overwrite</button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Conflict Prompt */}
      {rateConflictPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-2">Apply customer-specific rate?</h3>
            <p className="text-sm text-gray-700 mb-4">
              This line already has a non-zero rate from the grid/import. Choose whether to keep that rate or use the customer-specific rate for {rateConflictPrompt.itemName || 'this item'}.
            </p>
            {(rateConflictPrompt.customerName || rateConflictPrompt.serviceDate || rateConflictPrompt.lineNumber) && (
              <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm mb-3 space-y-1">
                {rateConflictPrompt.customerName && <div><span className="font-medium">Customer:</span> {rateConflictPrompt.customerName}</div>}
                {rateConflictPrompt.serviceDate && <div><span className="font-medium">Date:</span> {rateConflictPrompt.serviceDate}</div>}
                {rateConflictPrompt.lineNumber && <div><span className="font-medium">Line:</span> {rateConflictPrompt.lineNumber}</div>}
              </div>
            )}
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm mb-4 space-y-1">
              <div><span className="font-medium">Current rate:</span> {formatRate(rateConflictPrompt.currentRate)}</div>
              <div><span className="font-medium">Customer-specific rate:</span> {formatRate(rateConflictPrompt.effectiveRate)}</div>
            </div>
            <div className="flex gap-2 justify-end">
              <button className="rounded-xl border px-3 py-1 text-sm bg-white hover:bg-gray-50" onClick={() => resolveRateConflictPrompt('keep')}>
                Keep {formatRate(rateConflictPrompt.currentRate)}
              </button>
              <button className="rounded-xl bg-sedra px-3 py-1 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra" onClick={() => resolveRateConflictPrompt('use')}>
                Use {formatRate(rateConflictPrompt.effectiveRate)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Cell Modal */}
      {splitCellModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Split Cell Content</h3>
            <textarea className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 mb-4" placeholder="Enter services separated by semicolons..." rows={4} />
            <div className="flex gap-2">
              <button onClick={() => confirmSplitCell('', ';')} className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">Split</button>
              <button onClick={() => setSplitCellModal(null)} className="rounded-xl border px-4 py-2 text-sm bg-white hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Selection Modal */}
      {customerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Select Customer</h3>
            <p className="text-sm text-gray-600 mb-4">Customer "{customerModal.customerKey}" not found. Please select an existing customer or create a new one.</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {customers?.map((customer: any) => (
                <button key={customer.id} onClick={() => { setSpreadsheetRows(prev => prev.map(row => row.rowId === customerModal.rowId ? { ...row, customerId: customer.id, customerLookupKey: customer.display_name } : row)); setCustomerModal(null); }} className="w-full text-left px-3 py-2 rounded-xl hover:bg-gray-50">{customer.display_name}</button>
              ))}
            </div>
            <div className="mt-4 flex justify-between">
              <button onClick={() => { setPendingCustomerRowId(customerModal.rowId); setIsCustomerDrawerOpen(true); }} className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">+ New Customer</button>
              <button onClick={() => setCustomerModal(null)} className="rounded-xl border px-4 py-2 text-sm bg-white hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Drawer */}
      <CustomerFormDrawer
        isOpen={isCustomerDrawerOpen}
        onClose={() => { setIsCustomerDrawerOpen(false); setPendingCustomerRowId(null); }}
        onSave={async (payload) => {
          try {
            const newCustomer = await api.post<any>(`/customers`, payload);
            setSpreadsheetRows(prev => prev.map(r => r.rowId === pendingCustomerRowId ? { ...r, customerId: newCustomer.id, customerLookupKey: newCustomer.display_name } : r));
            setIsCustomerDrawerOpen(false);
            setPendingCustomerRowId(null);
          } catch (e) {
            alert('Failed to create customer');
          }
        }}
      />

      {/* Item Drawer */}
      <ItemFormDrawer
        isOpen={isItemDrawerOpen}
        onClose={async () => { setIsItemDrawerOpen(false); setItemDrawerInitial(''); await mutateItems(); customerItemsCacheRef.current = new Map(); refreshGridCells(); pendingPlaceholderRef.current = null; }}
        item={null}
        categories={Array.isArray(categories) ? categories : []}
        initialName={itemDrawerInitial}
        onSaved={async (saved) => {
          // Replace placeholder at the stored ref
          const ref = pendingPlaceholderRef.current;
          if (!ref) return;
          // Capture current row to find customer for effective rate
          const currentRow = spreadsheetRows.find(r => r.rowId === ref.rowId);
          const customerIdForRate = currentRow?.customerId || null;
          setSpreadsheetRows(prev => prev.map(r => {
            if (r.rowId !== ref.rowId) return r;
            const list: ServiceEntry[] = (r as any)[ref.dateKey] || [];
            const next = [...list];
            const line = { ...next[ref.index] } as ServiceEntry;
            line.type = saved.id;
            line.pendingName = undefined;
            line.needsInput = false;
            next[ref.index] = line;
            return { ...r, [ref.dateKey]: next } as any;
          }));
          // Apply effective rate if not specified (rate is 0)
          try {
            const refNow = pendingPlaceholderRef.current; // still the same placeholder context
            const shouldApplyRate = (() => {
              const row = spreadsheetRows.find(r => r.rowId === ref.rowId);
              const list: ServiceEntry[] = ((row as any)?.[ref.dateKey]) || [];
              const existing = list[ref.index];
              return !existing || !existing.rate || existing.rate === 0;
            })();
            if (shouldApplyRate) {
              const eff = await getEffectiveRate(customerIdForRate, saved.id);
              if (eff) {
                setSpreadsheetRows(prev => prev.map(r => {
                  if (r.rowId !== ref.rowId) return r;
                  const list: ServiceEntry[] = (r as any)[ref.dateKey] || [];
                  const next = [...list];
                  const line = { ...next[ref.index] } as ServiceEntry;
                  line.rate = eff.rate < 0 ? 0 : eff.rate;
                  (line as any).isTaxable = (eff as any).is_taxable ?? (eff as any).isTaxable ?? true;
                  next[ref.index] = line;
                  return { ...r, [ref.dateKey]: next } as any;
                }));
              }
            }
          } catch {}
          // Refresh price-aware rates
          await mutateItems();
          customerItemsCacheRef.current = new Map();
          refreshGridCells();
          resetRowHeights();
          pendingPlaceholderRef.current = null;
        }}
      />
    </div>
  );
} 
